import { useCacheSlice } from '../hooks'
import type { ApiClient } from '../types'
import type { BillingCategory, BillableCategory } from './matters'
import { fetchEntryFilters, getEntryFilterTimekeeperIds } from './entryFilters'

export type EntryStatus = 'tracked' | 'reviewed' | 'exported' | 'deleted'

export type EntryMatter = {
  id: string | null
  code: string | null
  description: string | null
  client: string | null
}

export type EntryCodes = {
  task: string | null
  activity: string | null
}

/**
 * Canonical v2 entry shape — server-side hands this back ready-to-render. No
 * normalization, no snake_case, no fake-UTC Dates. The shape matches
 * `entryListItemSchema` in the backend (`backend/src/v2/routes/entries/mapper.ts`).
 */
export type EntryV2 = {
  object: 'entry'
  id: string                  // "ent_<n>"
  status: EntryStatus
  date: string                // "YYYY-MM-DD"
  startTime: string | null    // "HH:mm" wall-clock
  endTime: string | null      // "HH:mm"
  units: number
  unitsSeconds: number | null
  description: string
  application: string | null
  documentName: string | null
  /** Timekeeper professional id for manager/timekeeper entry views. */
  timekeeperId?: string | null
  billable: boolean | null
  billingCategory: BillingCategory
  billableCategory: BillableCategory
  matter: EntryMatter | null
  codes: EntryCodes
}

/** Stripe-style list envelope returned by `GET /v2/entries`. */
type ListPage = {
  object: 'list'
  url: string
  hasMore: boolean
  data: EntryV2[]
}

/**
 * Page size used by the cache loop. Sized to comfortably hold the typical
 * user's full dataset in a single roundtrip — most users come back in one
 * page and skip the cursor entirely. Matches the endpoint's `max` cap.
 */
const PAGE_LIMIT = 5000

/** Hard ceiling so a misbehaving server can't pin this loop forever. */
const MAX_PAGES = 200

/**
 * Walks /v2/entries with `startingAfter` until the server reports
 * `hasMore: false`. The cache mirrors v1's "all entries for this user" model so
 * downstream consumers (filter chips, stats bar, fuse search) keep working
 * without thinking about pagination.
 */
export async function fetchAllEntries(api: ApiClient): Promise<EntryV2[]> {
  const out: EntryV2[] = []
  let startingAfter: string | null = null
  let timekeeperIds: string[] = []
  try {
    const filters = await fetchEntryFilters(api)
    timekeeperIds = getEntryFilterTimekeeperIds(filters)
  } catch {
    // If the filters endpoint is unavailable, keep the existing solo-user
    // behavior for /v2/entries instead of failing the entries cache entirely.
    timekeeperIds = []
  }
  for (let page = 0; page < MAX_PAGES; page++) {
    const params = new URLSearchParams({ limit: String(PAGE_LIMIT) })
    if (startingAfter) params.set('startingAfter', startingAfter)
    if (timekeeperIds.length > 1) params.set('timekeeperIds', JSON.stringify(timekeeperIds))
    const res = await api.makeApiRequest({
      endpoint: `/v2/entries?${params.toString()}`,
      method: 'GET',
    })
    const body = res.data as ListPage | undefined
    if (!body || !Array.isArray(body.data)) break
    out.push(...body.data)
    if (!body.hasMore || body.data.length === 0) return out
    startingAfter = body.data[body.data.length - 1].id
  }
  return out
}

export function useEntriesV2Cache() {
  const { data, isLoading, lastFetchedAt } = useCacheSlice('entries_v2')
  const entries = data as EntryV2[]
  // Match the convention used by other slices: only flag initial-load so
  // background refetches don't flicker derived UI.
  const isInitialLoading = isLoading && lastFetchedAt === 0
  return { entries, isLoading: isInitialLoading, lastFetchedAt }
}
